/**
 * features/71c/SessionReviewPanel.tsx — MER Algo Footprints v7.1C
 *
 * Session Review Workflow panel.
 * Read-only over journal71aEntries from the Zustand store.
 *
 * Views:
 *   Summary   — operator summary card + executive summary + session tags
 *   Setups    — attribution outcome report table (all taken/skipped/viewed entries)
 *   Narrative — deterministic template-based session story
 *
 * Never modifies store, engine, or ranking.
 * Export actions call export71c.ts helpers (client-side only).
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import {
  groupBySession, buildSessionSummary,
  buildOutcomeReport, buildSessionNarrative, SessionSummary,
} from './sessionUtils';

// ─── Inline discipline score computation (mirrors DisciplineScorePanel logic) ─
// We re-derive here to keep SessionReviewPanel self-contained and avoid
// importing DisciplineScorePanel's internal non-exported compute function.
// This is a read-only derivation — identical rules, zero engine impact.

import { Journal71AEntry } from '../../types';

function deriveDiscipline(entries: Journal71AEntry[]): { score: number; grade: string } {
  if (!entries.length) return { score: 0, grade: 'No Data' };

  const r1 = entries.filter(e => e.actionType === 'note_added').length;
  const r2 = entries.filter(e => e.actionType === 'outcome_reviewed').length;
  const r3 = entries.filter(e => e.actionType === 'trade_skipped' && (e.score ?? 100) < 45).length;
  const r4 = entries.filter(e => e.actionType === 'setup_viewed').length;
  const p1 = entries.filter(e => e.actionType === 'trade_taken' && (e.score ?? 100) < 45 && !e.note).length;
  const takenHigh = entries.filter(e => e.actionType === 'trade_taken' && (e.score ?? 0) >= 65);
  const outcomeSyms = new Set(entries.filter(e => e.actionType === 'outcome_reviewed').map(e => e.symbol));
  const p2 = takenHigh.filter(e => !outcomeSyms.has(e.symbol)).length;

  const raw = r1*3 + r2*4 + r3*2 + r4*2 + p1*(-3) + p2*(-1);
  const score = Math.min(100, Math.max(0, raw));
  const grade = score >= 80 ? 'Elite' : score >= 60 ? 'Strong' : score >= 35 ? 'Developing' : 'Needs Work';
  return { score, grade };
}

// ─── Style helpers ────────────────────────────────────────────────────────────

function scoreColor(v: number | null): string {
  if (v === null) return 'var(--t3)';
  return v >= 70 ? 'var(--green)' : v >= 45 ? 'var(--amber)' : 'var(--red)';
}

function outcomeColor(o: string): string {
  return o === 'win' ? 'var(--green)' : o === 'loss' ? 'var(--red)'
    : o === 'breakeven' ? 'var(--amber)' : 'var(--t3)';
}

function gradeColor(g: string): string {
  return g === 'Elite' ? 'var(--green)' : g === 'Strong' ? 'var(--cyan)'
    : g === 'Developing' ? 'var(--amber)' : g === 'Needs Work' ? 'var(--red)' : 'var(--t3)';
}

const TAG_COLOR: Record<string, string> = {
  'disciplined':      'var(--green)',
  'well-annotated':   'var(--cyan)',
  'selective':        'var(--cyan)',
  'breakout-heavy':   'var(--purple)',
  'range-bound':      'var(--blue)',
  'liquidity-driven': 'var(--gold)',
  'review-incomplete':'var(--amber)',
  'watchlist-heavy':  'var(--blue-dim)',
  'no-activity':      'var(--t3)',
  'standard-session': 'var(--t3)',
};

// ─── Session selector ─────────────────────────────────────────────────────────

function SessionSelector({ sessions, selected, onSelect }: {
  sessions: Array<{ label: string; entries: { length: number } }>;
  selected: string;
  onSelect: (s: string) => void;
}) {
  if (!sessions.length) return null;
  const sel: React.CSSProperties = {
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 3, color: 'var(--text)', fontFamily: 'var(--mono)',
    fontSize: 11, padding: '4px 8px', outline: 'none', cursor: 'pointer',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <span style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
        textTransform: 'uppercase', letterSpacing: '.5px' }}>Session</span>
      <select value={selected} onChange={e => onSelect(e.target.value)} style={sel}>
        <option value="__all__">All time ({sessions.reduce((n, s) => n + s.entries.length, 0)} entries)</option>
        {sessions.map(s => (
          <option key={s.label} value={s.label}>
            {s.label} ({s.entries.length} entries)
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── KPI tile ─────────────────────────────────────────────────────────────────

function Tile({ label, value, color, sub }: {
  label: string; value: string | number; color?: string; sub?: string;
}) {
  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '8px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 8, color: 'var(--t3)', textTransform: 'uppercase',
        letterSpacing: '.6px', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color ?? 'var(--text)',
        fontFamily: 'var(--mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ s }: { s: SessionSummary }) {
  return (
    <div>
      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
        <Tile label="Total"    value={s.totalActions} />
        <Tile label="Taken"    value={s.taken}    color="var(--green)"  sub={s.selectionRate} />
        <Tile label="Skipped"  value={s.skipped}  color="var(--amber)" />
        <Tile label="Notes"    value={s.noted}    color="var(--cyan)"  />
        <Tile label="Outcomes" value={s.outcomes} color="var(--purple)" sub={s.outcomeCompletion} />
      </div>

      {/* Outcomes + discipline */}
      {(s.wins + s.losses + s.breakevens > 0 || s.disciplineScore > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
          {(s.wins + s.losses + s.breakevens > 0) && (
            <div style={{
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 4, padding: '8px 10px',
            }}>
              <div style={{ fontSize: 8, color: 'var(--t3)', textTransform: 'uppercase',
                letterSpacing: '.6px', marginBottom: 5 }}>Recorded Outcomes</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <span style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 700 }}>{s.wins}W</span>
                <span style={{ color: 'var(--red)',   fontFamily: 'var(--mono)', fontWeight: 700 }}>{s.losses}L</span>
                <span style={{ color: 'var(--amber)', fontFamily: 'var(--mono)', fontWeight: 700 }}>{s.breakevens}BE</span>
              </div>
            </div>
          )}
          <div style={{
            background: 'var(--surface2)', border: `1px solid ${gradeColor(s.disciplineGrade)}44`,
            borderRadius: 4, padding: '8px 10px',
          }}>
            <div style={{ fontSize: 8, color: 'var(--t3)', textTransform: 'uppercase',
              letterSpacing: '.6px', marginBottom: 5 }}>Discipline</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: gradeColor(s.disciplineGrade),
                fontFamily: 'var(--mono)' }}>{s.disciplineScore}</span>
              <span style={{ fontSize: 10, color: gradeColor(s.disciplineGrade) }}>{s.disciplineGrade}</span>
            </div>
          </div>
        </div>
      )}

      {/* Top patterns row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 12 }}>
        {[
          { label: 'Top Symbols',    items: s.topSymbols },
          { label: 'Top Setup Types',items: s.topSetupTypes },
          { label: 'Top Regimes',    items: s.topRegimes },
        ].map(({ label, items }) => (
          <div key={label} style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 4, padding: '8px 10px',
          }}>
            <div style={{ fontSize: 8, color: 'var(--t3)', textTransform: 'uppercase',
              letterSpacing: '.6px', marginBottom: 5 }}>{label}</div>
            {items.length ? items.map(({ value, count }) => (
              <div key={value} style={{ display: 'flex', justifyContent: 'space-between',
                fontSize: 9, marginBottom: 2 }}>
                <span style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>
                  {value.replace(/_/g,' ')}
                </span>
                <span style={{ color: 'var(--t3)' }}>×{count}</span>
              </div>
            )) : (
              <div style={{ fontSize: 9, color: 'var(--t3)', fontStyle: 'italic' }}>—</div>
            )}
          </div>
        ))}
      </div>

      {/* Session tags */}
      {s.sessionTags.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {s.sessionTags.map(tag => (
            <span key={tag} style={{
              fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
              color: TAG_COLOR[tag] ?? 'var(--blue-dim)',
              border: `1px solid ${TAG_COLOR[tag] ?? 'var(--border)'}55`,
              background: `${TAG_COLOR[tag] ?? 'var(--blue-dim)'}10`,
              padding: '2px 7px', borderRadius: 10, letterSpacing: '.3px',
            }}>{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Outcome report table ─────────────────────────────────────────────────────

function OutcomeReportTable({ rows }: { rows: ReturnType<typeof buildOutcomeReport> }) {
  if (!rows.length) {
    return (
      <div style={{ fontSize: 10, color: 'var(--t3)', padding: '16px 0', fontStyle: 'italic' }}>
        No taken, skipped, or viewed setups to report. Log activity via ▼ ATTRIBUTION.
      </div>
    );
  }

  const TH: React.CSSProperties = {
    fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--t3)',
    textTransform: 'uppercase', letterSpacing: '.5px', padding: '5px 7px',
    borderBottom: '1px solid var(--border)', textAlign: 'left', whiteSpace: 'nowrap',
  };
  const TD: React.CSSProperties = {
    fontSize: 9, fontFamily: 'var(--mono)', padding: '5px 7px',
    borderBottom: '1px solid var(--surface)', verticalAlign: 'top',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>Time</th>
            <th style={TH}>Symbol</th>
            <th style={TH}>Setup</th>
            <th style={TH}>Side</th>
            <th style={TH}>Score</th>
            <th style={TH}>Regime</th>
            <th style={TH}>Gamma</th>
            <th style={TH}>Action</th>
            <th style={TH}>Note</th>
            <th style={TH}>Outcome</th>
            <th style={TH}>Snap#</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'transparent' }}>
              <td style={{ ...TD, color: 'var(--t3)', fontSize: 8 }}>{r.loggedAt}</td>
              <td style={{ ...TD, color: 'var(--gold)', fontWeight: 700 }}>{r.symbol}</td>
              <td style={{ ...TD, color: 'var(--text)' }}>{r.setupType}</td>
              <td style={{ ...TD, color: r.direction === 'long' ? 'var(--green)' : r.direction === 'short' ? 'var(--red)' : 'var(--t3)' }}>
                {r.direction}
              </td>
              <td style={{ ...TD, color: scoreColor(r.score), fontWeight: 700 }}>
                {r.score !== null ? r.score : '—'}
              </td>
              <td style={{ ...TD, color: 'var(--gold)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.regime}
              </td>
              <td style={{ ...TD, color: 'var(--blue-dim)', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.gammaStatus}
              </td>
              <td style={{ ...TD, fontWeight: 700, color:
                r.action.includes('TAKEN') ? 'var(--green)'
                : r.action.includes('SKIPPED') ? 'var(--amber)'
                : r.action.includes('VIEWED') ? 'var(--blue-dim)'
                : 'var(--purple)',
              }}>{r.action}</td>
              <td style={{ ...TD, color: r.hasNote ? 'var(--cyan)' : 'var(--t3)' }}>
                {r.hasNote ? (
                  <span title={r.notePreview}>✎</span>
                ) : '—'}
              </td>
              <td style={{ ...TD, color: outcomeColor(r.outcome), fontWeight: r.outcome !== '—' ? 700 : 400 }}>
                {r.outcome}
              </td>
              <td style={{ ...TD, color: r.hasSnapshot ? 'var(--cyan)' : 'var(--t3)' }}>
                {r.snapshotRank !== null ? `#${r.snapshotRank}` : (r.hasSnapshot ? '✓' : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

type SessionView = 'summary' | 'setups' | 'narrative';

export function SessionReviewPanel() {
  const allEntries = useStore(s => s.journal71aEntries);

  const [selectedSession, setSelectedSession] = useState<string>('__all__');
  const [view, setView] = useState<SessionView>('summary');

  const sessions = useMemo(() => groupBySession(allEntries), [allEntries]);

  const activeEntries = useMemo(() => {
    if (selectedSession === '__all__') return allEntries;
    return sessions.find(s => s.label === selectedSession)?.entries ?? [];
  }, [allEntries, sessions, selectedSession]);

  const { score: disciplineScore, grade: disciplineGrade } = useMemo(
    () => deriveDiscipline(activeEntries),
    [activeEntries],
  );

  const summary = useMemo(
    () => buildSessionSummary(activeEntries, disciplineScore, disciplineGrade),
    [activeEntries, disciplineScore, disciplineGrade],
  );

  const outcomeRows = useMemo(() => buildOutcomeReport(activeEntries), [activeEntries]);
  const narrative   = useMemo(() => buildSessionNarrative(summary), [summary]);

  if (!allEntries.length) {
    return (
      <div style={{ padding: '28px 0', color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 8 }}>
          📋 Session Review
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.7 }}>
          No activity logged yet.<br />
          <span style={{ color: 'var(--blue-dim)' }}>
            Dashboard → expand a scanner row → ▼ ATTRIBUTION<br />
            → log VIEWED, TAKEN, or SKIPPED to start building session data.
          </span>
        </div>
      </div>
    );
  }

  const tabBtn = (id: SessionView): React.CSSProperties => ({
    fontSize: 9, fontFamily: 'var(--mono)', fontWeight: view === id ? 700 : 400,
    color: view === id ? 'var(--gold)' : 'var(--t3)',
    background: view === id ? 'var(--gold)12' : 'none',
    border: `1px solid ${view === id ? 'var(--gold)55' : 'var(--border)'}`,
    padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
  });

  return (
    <div>
      <SessionSelector
        sessions={sessions}
        selected={selectedSession}
        onSelect={s => { setSelectedSession(s); setView('summary'); }}
      />

      {/* Sub-view tabs */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 12 }}>
        <button style={tabBtn('summary')}   onClick={() => setView('summary')}>Summary</button>
        <button style={tabBtn('setups')}    onClick={() => setView('setups')}>
          Setups ({outcomeRows.length})
        </button>
        <button style={tabBtn('narrative')} onClick={() => setView('narrative')}>Narrative</button>
      </div>

      {/* Summary view */}
      {view === 'summary' && <SummaryCard s={summary} />}

      {/* Attribution outcome report */}
      {view === 'setups' && <OutcomeReportTable rows={outcomeRows} />}

      {/* Deterministic narrative */}
      {view === 'narrative' && (
        <div>
          <div style={{
            fontSize: 8, fontFamily: 'var(--mono)', color: 'var(--blue-dim)',
            letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: 8,
            padding: '4px 8px', background: 'var(--surface)',
            border: '1px solid var(--border)', borderRadius: 3, display: 'inline-block',
          }}>
            DETERMINISTIC SUMMARY · TEMPLATE-BASED · NOT AI-GENERATED
          </div>
          <pre style={{
            fontSize: 10, color: 'var(--text)', lineHeight: 1.7,
            fontFamily: 'var(--sans)', whiteSpace: 'pre-wrap', margin: 0,
          }}>{narrative}</pre>
        </div>
      )}
    </div>
  );
}
